/**
 * API Client for EASI 2.0
 *
 * Provides authenticated HTTP client with:
 * - Automatic JWT token attachment
 * - Token refresh on 401 errors
 * - Request/response interceptors
 * - Error handling
 *
 * HIPAA Requirements:
 * - 164.312(e)(1): Transmission security
 * - 164.312(d): Person or entity authentication
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  AxiosError,
} from 'axios';
import { getAccessToken, getAuthSession, getIdToken } from '../auth/cognito-client';
import { config } from '../config/environment';

/**
 * API error response structure
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  requestId?: string;
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
  requestId?: string;
}

/**
 * Request configuration with retry options
 */
interface RequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  retryCount?: number;
  maxRetries?: number;
}

/**
 * Token refresh state to prevent multiple concurrent refreshes
 */
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

/**
 * Subscribe to token refresh
 */
function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers of new token
 */
function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

/**
 * Create the API client instance
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: config.apiEndpoint,
    timeout: 900000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - attach JWT token
  client.interceptors.request.use(
    async (requestConfig: InternalAxiosRequestConfig) => {
      // Record API activity for idle timer — a request means the system is working
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('easi-api-activity'));
      }

      const customConfig = requestConfig as InternalAxiosRequestConfig & RequestConfig;

      // Skip auth for public endpoints
      if (customConfig.skipAuth) {
        return requestConfig;
      }

      try {
        // Cognito authorizer (without OAuth scopes) expects the ID token
        const token = await getIdToken();
        if (token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }

      // Add request ID for tracing
      requestConfig.headers['X-Request-ID'] = generateRequestId();

      return requestConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle errors and token refresh
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Record API activity for idle timer — a successful response means active work
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('easi-api-activity'));
      }
      // Extract request ID from response headers
      const requestId = response.headers['x-request-id'] || response.config.headers['X-Request-ID'];
      if (requestId && response.data) {
        response.data.requestId = requestId;
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & RequestConfig;

      // Handle 401 Unauthorized - token expired
      if (error.response?.status === 401 && originalRequest && !originalRequest.retryCount) {
        if (isRefreshing) {
          // Wait for token refresh
          return new Promise((resolve) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(client(originalRequest));
            });
          });
        }

        originalRequest.retryCount = (originalRequest.retryCount || 0) + 1;
        isRefreshing = true;

        try {
          // Attempt to refresh the session (forceRefresh bypasses cache)
          const session = await getAuthSession(true);

          const newIdToken = session?.idToken;
          if (newIdToken) {
            // Verify token type — Cognito authorizer requires token_use: "id"
            try {
              const payload = JSON.parse(atob(newIdToken.split('.')[1]));
              console.log('[EASI Auth] Refresh succeeded', {
                token_use: payload.token_use,
                exp: new Date((payload.exp as number) * 1000).toISOString(),
              });
              if (payload.token_use !== 'id') {
                console.error('[EASI Auth] WARNING: token_use is', payload.token_use, '— expected "id"');
              }
            } catch { /* decode failed, proceed anyway */ }

            // Store in localStorage so getIdToken() on retry reads the NEW token
            localStorage.setItem('easi_id_token', newIdToken);
            if (session.accessToken) {
              localStorage.setItem('easi_access_token', session.accessToken);
            }

            // Update axios defaults so ALL subsequent requests use the new token
            client.defaults.headers.common['Authorization'] = `Bearer ${newIdToken}`;

            onTokenRefreshed(newIdToken);
            originalRequest.headers.Authorization = `Bearer ${newIdToken}`;
            return client(originalRequest);
          } else {
            console.log('[EASI Auth] Refresh returned no idToken — redirecting to login');
            // Session refresh failed - redirect to login
            handleAuthFailure();
            return Promise.reject(error);
          }
        } catch (refreshError: any) {
          // Distinguish network errors from genuine auth rejection.
          // Network errors (timeout, offline, DNS) should NOT destroy the session
          // — the refresh token may still be valid. Just fail this request and
          // let the next one retry. Only hard-redirect on confirmed auth failure.
          const isNetworkError = !refreshError?.response &&
            (refreshError?.code === 'ERR_NETWORK' ||
             refreshError?.code === 'ECONNABORTED' ||
             refreshError?.message?.includes('Network Error') ||
             refreshError?.message?.includes('timeout'));
          if (!isNetworkError) {
            handleAuthFailure();
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Transform error response — reject with a proper Error instance
      // so error boundaries and catch handlers receive an Error, not a plain object
      const apiError = transformError(error);
      const err = new Error(apiError.message);
      (err as any).code = apiError.code;
      (err as any).details = apiError.details;
      (err as any).requestId = apiError.requestId;
      return Promise.reject(err);
    }
  );

  return client;
}

/**
 * Handle authentication failure
 */
function handleAuthFailure(): void {
  // Save current location for post-login redirect — user returns here after re-login
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== '/' && currentPath !== '/login') {
      localStorage.setItem('easi_redirect_after_login', currentPath);
    }
  }

  // Clear all stored tokens/state to prevent redirect loops
  localStorage.removeItem('lastActivity');
  localStorage.removeItem('easi_id_token');
  localStorage.removeItem('easi_access_token');
  localStorage.removeItem('easi_refresh_token');

  // Redirect to login
  if (typeof window !== 'undefined') {
    window.location.href = '/login?session_expired=true';
  }
}

/**
 * Transform Axios error to API error
 */
function transformError(error: AxiosError): ApiError {
  const requestId = error.config?.headers?.['X-Request-ID'] as string | undefined;

  if (error.response) {
    // Server responded with error
    const data = error.response.data as Record<string, unknown>;
    // Lambda error envelope nests message in data.error.message
    const errorObj = data?.error as Record<string, unknown> | undefined;
    return {
      code: (errorObj?.code as string) || (data?.code as string) || `HTTP_${error.response.status}`,
      message: (errorObj?.message as string) || (data?.message as string) || getStatusMessage(error.response.status),
      details: (errorObj?.details as Record<string, unknown>) || (data?.details as Record<string, unknown>),
      requestId,
    };
  }

  if (error.request) {
    // Request made but no response received
    return {
      code: 'NETWORK_ERROR',
      message: 'Unable to reach the server. Please check your connection.',
      requestId,
    };
  }

  // Request setup error
  return {
    code: 'REQUEST_ERROR',
    message: error.message || 'An unexpected error occurred.',
    requestId,
  };
}

/**
 * Get human-readable message for HTTP status code
 */
function getStatusMessage(status: number): string {
  const messages: Record<number, string> = {
    400: 'Invalid request. Please check your input.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'This operation conflicts with an existing resource.',
    422: 'The provided data is invalid.',
    429: 'Too many requests. Please try again later.',
    500: 'An internal server error occurred.',
    502: 'The server is temporarily unavailable.',
    503: 'The service is temporarily unavailable.',
    504: 'The server took too long to respond.',
  };

  return messages[status] || 'An unexpected error occurred.';
}

/**
 * Generate unique request ID for tracing
 */
function generateRequestId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `req_${timestamp}_${random}`;
}

// Create singleton client instance
const apiClient = createApiClient();

/**
 * Unwrap the Lambda response envelope.
 *
 * Lambda responses use a standard envelope: {success, message, data, timestamp}.
 * This extracts the inner `data` so callers get the actual payload.
 */
function unwrapResponse<T>(response: { data: unknown; status: number }): ApiResponse<T> {
  const body = response.data as Record<string, unknown> | null;
  const isEnvelope = body && typeof body === 'object' && 'success' in body && 'data' in body;
  return {
    data: (isEnvelope ? body.data : body) as T,
    status: response.status,
    requestId: (body?.requestId as string) ?? undefined,
  };
}

/**
 * Type-safe GET request
 */
export async function get<T>(
  url: string,
  config?: RequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.get<T>(url, config);
  return unwrapResponse<T>(response);
}

/**
 * Type-safe POST request
 */
export async function post<T>(
  url: string,
  data?: unknown,
  config?: RequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.post<T>(url, data, config);
  return unwrapResponse<T>(response);
}

/**
 * Type-safe PUT request
 */
export async function put<T>(
  url: string,
  data?: unknown,
  config?: RequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.put<T>(url, data, config);
  return unwrapResponse<T>(response);
}

/**
 * Type-safe PATCH request
 */
export async function patch<T>(
  url: string,
  data?: unknown,
  config?: RequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.patch<T>(url, data, config);
  return unwrapResponse<T>(response);
}

/**
 * Type-safe DELETE request
 */
export async function del<T>(
  url: string,
  config?: RequestConfig
): Promise<ApiResponse<T>> {
  const response = await apiClient.delete<T>(url, config);
  return unwrapResponse<T>(response);
}

/**
 * Upload file with progress tracking
 */
export async function uploadFile<T>(
  url: string,
  file: File,
  onProgress?: (progress: number) => void,
  config?: RequestConfig
): Promise<ApiResponse<T>> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<T>(url, formData, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });

  return unwrapResponse<T>(response);
}

/**
 * Download file
 */
export async function downloadFile(
  url: string,
  filename: string,
  config?: RequestConfig
): Promise<void> {
  const response = await apiClient.get(url, {
    ...config,
    responseType: 'blob',
  });

  // Create download link
  const blob = new Blob([response.data]);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

export { apiClient };
export default apiClient;
